import { Axis } from '@visx/axis';
import { Group } from '@visx/group';
import ParentSize from '@visx/responsive/lib/components/ParentSize';
import { scaleLinear } from '@visx/scale';
import { Line, LinePath } from '@visx/shape';
import { max } from 'd3';

import { Card } from '../../../components/Overview/Card';
import { COMMON_AXIS_PROPS } from './const';

type DataPoint = {
  x: number;
  y: number;
};

type Props = {
  currentSelectedIndex: number;
  data: DataPoint[];
  title: string;
  tickFormat: (value: number) => string;
} & {
  parentWidth?: number;
  parentHeight?: number;
};

export function ChartContainer({
  currentSelectedIndex,
  data,
  title,
  tickFormat,
}: Props) {
  return (
    <Card className="h-60 overflow-hidden">
      <h2 className="text-center">{title}</h2>
      <ParentSize debounceTime={10}>
        {({ width, height }) => {
          const axisWidth = window.screen.availWidth > 2000 ? 110 : 55;

          const canvasWidth = width - axisWidth;

          const xDomain = data.map((d) => d.x) as number[];

          const xScale = scaleLinear<number>({
            domain: [0, xDomain[xDomain.length - 1]],
          });

          const yScale = scaleLinear<number>({
            domain: [0, (max(data, (d) => d.y) ?? 0) * 1.1],
            nice: true,
          });

          xScale.range([0, canvasWidth]);
          yScale.range([height, 0]);

          return (
            <svg width={width} height={height}>
              <Group top={0} left={axisWidth}>
                <Axis
                  {...COMMON_AXIS_PROPS}
                  scale={yScale}
                  orientation="left"
                  numTicks={5}
                  tickFormat={(value) => tickFormat(value.valueOf())}
                  tickLabelProps={(tick, index, allTicks) => {
                    let verticalAnchor: 'start' | 'middle' | 'end';

                    if (index === 0) {
                      verticalAnchor = 'end';
                    } else if (index === allTicks.length - 1) {
                      verticalAnchor = 'start';
                    } else {
                      verticalAnchor = 'middle';
                    }

                    return {
                      dx: '-0.25rem',
                      textAnchor: 'end' as const,
                      verticalAnchor,
                    };
                  }}
                />
                <Line
                  className="stroke-neutral-50"
                  from={{ x: xScale(currentSelectedIndex), y: 0 }}
                  to={{ x: xScale(currentSelectedIndex), y: height }}
                />
                <LinePath<DataPoint>
                  className="stroke-fuschia-60"
                  data={data}
                  defined={(d) => d.y != null}
                  strokeWidth={2}
                  x={(d) => xScale(d.x) ?? 0}
                  y={(d) => yScale(d.y) as number}
                />
              </Group>
            </svg>
          );
        }}
      </ParentSize>
    </Card>
  );
}
